import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listAppSecrets,
  saveAppSecret,
  deleteAppSecret,
} from "@/lib/admin.functions";

// ═══════════════════════════════════════════════════════════════
// API Keys tab
// ═══════════════════════════════════════════════════════════════

export function ApiKeysTab() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["admin-app-secrets"], queryFn: () => listAppSecrets() });
  const save = useMutation({
    mutationFn: (v: { name: string; value: string }) =>
      saveAppSecret({ data: v as any }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-app-secrets"] }),
  });
  const del = useMutation({
    mutationFn: (name: string) => deleteAppSecret({ data: { name } as any }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-app-secrets"] }),
  });

  return (
    <div className="space-y-4">
      <div className="card-glass">
        <h3 className="text-base font-semibold">Chaves de API</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Salve as chaves aqui para editá-las em runtime, sem redeploy. Valores em branco caem para
          a variável de ambiente correspondente (se houver). Nunca compartilhe estes valores.
        </p>
      </div>

      {list.isLoading && <p className="text-sm text-muted-foreground">Carregando...</p>}

      <div className="grid gap-4 md:grid-cols-2">
        {(list.data ?? []).map((item) => (
          <SecretCard
            key={item.name}
            item={item}
            onSave={(value) => save.mutateAsync({ name: item.name, value })}
            onDelete={() => del.mutateAsync(item.name)}
          />
        ))}
      </div>
    </div>
  );
}

function SecretCard({
  item,
  onSave,
  onDelete,
}: {
  item: {
    name: string;
    label: string;
    where: string;
    configured: boolean;
    source: "db" | "env" | "none";
    masked: string;
    updated_at: string | null;
  };
  onSave: (value: string) => Promise<any>;
  onDelete: () => Promise<any>;
}) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const badge = item.source === "db"
    ? { label: "Salva no banco", cls: "bg-primary/15 text-primary" }
    : item.source === "env"
      ? { label: "Fallback env", cls: "bg-amber-500/15 text-amber-600" }
      : { label: "Não configurada", cls: "bg-muted text-muted-foreground" };

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!value.trim()) return;
    setSaving(true);
    setMsg(null);
    try {
      await onSave(value.trim());
      setValue("");
      setMsg("Salvo.");
    } catch (err) {
      setMsg((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card-glass space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold">{item.label}</p>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{item.name}</p>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs ${badge.cls}`}>{badge.label}</span>
      </div>
      <p className="text-xs text-muted-foreground">{item.where}</p>
      <div className="rounded-lg bg-secondary/40 px-3 py-2 font-mono text-xs">
        {item.configured ? item.masked : "— nenhuma chave —"}
      </div>
      {item.updated_at && (
        <p className="text-[11px] text-muted-foreground">
          Atualizada em {new Date(item.updated_at).toLocaleString("pt-BR")}
        </p>
      )}
      <form onSubmit={handleSave} className="space-y-2">
        <input
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Cole a nova chave para atualizar"
          className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm"
          autoComplete="new-password"
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={saving || !value.trim()}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {saving ? "Salvando..." : "Salvar"}
          </button>
          {item.source === "db" && (
            <button
              type="button"
              onClick={async () => {
                if (!confirm(`Remover ${item.label}? O fallback env, se houver, volta a ser usado.`)) return;
                try {
                  await onDelete();
                  setMsg("Removida.");
                } catch (err) {
                  setMsg((err as Error).message);
                }
              }}
              className="rounded-lg border border-destructive/60 px-3 py-2 text-sm text-destructive"
            >
              Remover
            </button>
          )}
        </div>
        {msg && <p className="text-xs text-muted-foreground">{msg}</p>}
      </form>
    </div>
  );
}
